// src/utils/suiteProblemes.ts — SCE Issues
// « Problème suivant » (01/10, piste A de la maquette
// https://claude.ai/artifact/A1CcJPk5w1XRhL6YwRj2jo) : la liste que l'on
// parcourt depuis une fiche ouverte en surimpression, et où l'on en est.
//
// ─── Quelle liste ───
//
// Celle d'où l'on vient, telle qu'elle était affichée (filtres, tri, recherche)
// au moment du clic : elle voyage dans l'état d'historique, FIGÉE. Sinon un
// problème qualifié sortirait de « À qualifier » sous les pieds du
// coordinateur, et « suivant » sauterait une fiche.
//
// Sans liste d'origine (lien de mail, fil d'activité, recherche, fiche sœur) :
// tous les problèmes, dans l'ordre de « Tous les problèmes » sans filtre.
//
// Pur : ni React, ni routeur — `tests/suiteProblemes.test.ts`.

import { compteCommeProbleme, type Issue } from '@/types/issue.types'

export interface SuiteProblemes {
  /** Ce qu'on parcourt, affiché au-dessus de la fiche : « À qualifier », « Mes déclarations »… */
  libelle: string
  /** Les ID des problèmes (`P2026-0042`), dans l'ordre de la liste d'origine. */
  ids: readonly string[]
}

export interface PositionSuite {
  /** Rang de la fiche, à partir de 1. */
  rang: number
  total: number
  precedent: string | null
  suivant: string | null
}

export const LIBELLE_SUITE_GLOBALE = 'Tous les problèmes'

/** La suite d'une liste affichée, dans son ordre. */
export function suiteDe(libelle: string, issues: readonly Pick<Issue, 'id'>[]): SuiteProblemes {
  return { libelle, ids: issues.map((i) => i.id) }
}

/**
 * Tous les problèmes, comme « Tous les problèmes » sans filtre : les annulés
 * en sont exclus, l'ordre de lecture (déclaration la plus récente d'abord)
 * est gardé.
 */
export function suiteGlobale(tous: readonly Issue[]): SuiteProblemes {
  return suiteDe(LIBELLE_SUITE_GLOBALE, tous.filter(compteCommeProbleme))
}

/**
 * Relit une suite déposée dans l'état d'historique. Tolérante : un état
 * abîmé, ou laissé par une version précédente de l'appli, donne null plutôt
 * qu'une erreur.
 */
export function lireSuite(valeur: unknown): SuiteProblemes | null {
  if (typeof valeur !== 'object' || valeur === null) return null
  const { libelle, ids } = valeur as { libelle?: unknown; ids?: unknown }
  if (typeof libelle !== 'string' || !Array.isArray(ids)) return null
  if (!ids.every((id): id is string => typeof id === 'string')) return null
  return { libelle, ids }
}

/** Où se trouve la fiche dans la suite ; null si elle n'y est pas. */
export function positionDans(suite: SuiteProblemes, id: string): PositionSuite | null {
  const index = suite.ids.indexOf(id)
  if (index < 0) return null
  return {
    rang: index + 1,
    total: suite.ids.length,
    precedent: index > 0 ? suite.ids[index - 1]! : null,
    suivant: index < suite.ids.length - 1 ? suite.ids[index + 1]! : null,
  }
}

/**
 * La suite à parcourir depuis cette fiche : celle de la liste d'origine si la
 * fiche en fait partie, sinon tous les problèmes, sinon rien (un annulé ouvert
 * depuis la recherche, par exemple, n'est dans aucune).
 */
export function suiteDeLaFiche(
  origine: SuiteProblemes | null,
  tous: readonly Issue[],
  id: string,
): SuiteProblemes | null {
  if (origine && origine.ids.includes(id)) return origine
  const globale = suiteGlobale(tous)
  return globale.ids.includes(id) ? globale : null
}
